import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { X, Zap, AlertTriangle, CheckCircle, Loader2 } from "lucide-react";
import { isEnphaseUser } from "../../../lib/utils/utility";

const MIN_CHARGE_LEVEL = 6;

const StartChargingModal = ({ isOpen, onClose, onSubmit, validationState, ratedCurrent }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [chargeLevel, setChargeLevel] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setConfirmed(false);
      setChargeLevel(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const {
    loading = false,
    error = null,
    stationName = null,
    maxBreakerCurrent = null,
    currentLoad = null,
    chargerMaxCurrent = null,
    hasCapacity = null,
    skipCheck = false,
  } = validationState || {};

  const sliderMax = ratedCurrent || chargerMaxCurrent || MIN_CHARGE_LEVEL;
  const effectiveChargeLevel = chargeLevel ?? sliderMax;

  const remainingCapacity = maxBreakerCurrent != null && currentLoad != null ? maxBreakerCurrent - currentLoad : null;

  const handleSubmit = () => {
    onSubmit(effectiveChargeLevel);
    setConfirmed(false);
    setChargeLevel(null);
    onClose();
  };

  const handleClose = () => {
    setConfirmed(false);
    setChargeLevel(null);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3>Start Charging</h3>
          <button className="modal-close-btn" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">
          {loading && (
            <div className="charging-modal__loading">
              <Loader2 size={18} className="spin-animation" />
              <span>Validating breaker capacity...</span>
            </div>
          )}

          {error && (
            <div className="charging-modal__alert charging-modal__alert--error">
              <AlertTriangle size={16} />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && skipCheck && (
            <div className="charging-modal__alert charging-modal__alert--info">
              <Zap size={16} />
              <span>This site is not assigned to any station. Breaker capacity check skipped.</span>
            </div>
          )}

          {!loading && !error && !skipCheck && hasCapacity !== null && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Station Info */}
              <div className="charging-modal__station-info">
                {!isEnphaseUser() && (
                  <div className="charging-modal__station-name">Station: {stationName || "Unknown"}</div>
                )}
                <div className="charging-modal__grid">
                  <div>
                    <span className="charging-modal__grid-label">Max Breaker Current: </span>
                    <strong>{maxBreakerCurrent}A</strong>
                  </div>
                  <div>
                    <span className="charging-modal__grid-label">Current Load: </span>
                    <strong>{currentLoad}A</strong>
                  </div>
                  <div>
                    <span className="charging-modal__grid-label">Charger Max Current: </span>
                    <strong>{chargerMaxCurrent}A</strong>
                  </div>
                  <div>
                    <span className="charging-modal__grid-label">Remaining Capacity: </span>
                    <strong style={{ color: remainingCapacity >= chargerMaxCurrent ? "#059669" : "#dc2626" }}>
                      {remainingCapacity}A
                    </strong>
                  </div>
                </div>
              </div>

              {/* Capacity Result */}
              {hasCapacity ? (
                <div className="charging-modal__alert charging-modal__alert--success">
                  <CheckCircle size={16} />
                  <span>
                    Sufficient capacity available. Adding {chargerMaxCurrent}A will bring total load to{" "}
                    {currentLoad + chargerMaxCurrent}A / {maxBreakerCurrent}A.
                  </span>
                </div>
              ) : (
                <div className="charging-modal__alert charging-modal__alert--warning">
                  <AlertTriangle size={16} />
                  <span>
                    <strong>Breaker capacity exceeded.</strong> Adding {chargerMaxCurrent}A would bring total to{" "}
                    {currentLoad + chargerMaxCurrent}A, exceeding the {maxBreakerCurrent}A breaker limit.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Charge Level Slider — shown when validation passes or check is skipped */}
          {!loading && !error && (hasCapacity || skipCheck) && (
            <div className="charging-modal__slider-wrap">
              <div className="charging-modal__slider-header">
                <span className="charging-modal__slider-label">Charge Level</span>
                <span className="charging-modal__slider-value">{effectiveChargeLevel}A</span>
              </div>
              <input
                type="range"
                min={MIN_CHARGE_LEVEL}
                max={sliderMax}
                step={1}
                value={effectiveChargeLevel}
                onChange={e => setChargeLevel(Number(e.target.value))}
                className="charging-modal__slider"
              />
              <div className="charging-modal__slider-range">
                <span>{MIN_CHARGE_LEVEL}A (min)</span>
                <span>{sliderMax}A (max)</span>
              </div>
            </div>
          )}

          {/* Confirmation checkbox when capacity is available or check is skipped */}
          {!loading && !error && (hasCapacity || skipCheck) && (
            <label className="charging-modal__confirm">
              <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
              <span>I confirm I want to start charging at {effectiveChargeLevel}A</span>
            </label>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-cancel" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="btn-submit"
            disabled={loading || (!hasCapacity && !skipCheck) || !confirmed}
            onClick={handleSubmit}
            style={{
              opacity: loading || (!hasCapacity && !skipCheck) || !confirmed ? 0.5 : 1,
              cursor: loading || (!hasCapacity && !skipCheck) || !confirmed ? "not-allowed" : "pointer",
            }}
          >
            Start Charging
          </button>
        </div>
      </div>
    </div>
  );
};

StartChargingModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  validationState: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.string,
    stationName: PropTypes.string,
    maxBreakerCurrent: PropTypes.number,
    currentLoad: PropTypes.number,
    chargerMaxCurrent: PropTypes.number,
    hasCapacity: PropTypes.bool,
    skipCheck: PropTypes.bool,
  }),
  ratedCurrent: PropTypes.number,
};

export default StartChargingModal;
